import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatNumberSigFig } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import { MealsProducedChart } from '../FisheryCharts/FisheryCharts';
import InsightColumnPair from '../InsightColumnPair';
import './LandedMeals.css';

interface InsightData {
  mealsProduced?: number;
  mealsUncertainty?: number;
}

interface LandedMealsData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
}

function filterMealsByYear(meals: unknown[] | undefined, selectedYear: string): unknown[] {
  if (!meals || !Array.isArray(meals)) return [];
  const withYear = (item: unknown): number => (item as { year?: number })?.year ?? 0;
  if (selectedYear === 'historical') return meals.filter((item) => withYear(item) <= 2024);
  if (selectedYear === 'projections') return meals.filter((item) => withYear(item) >= 2025);
  return meals;
}

function isAllZero(data: unknown[], keys = ['actual', 'potential']): boolean {
  if (!data || !data.length) return true;
  return data.every((d) => {
    const obj = d as Record<string, unknown>;
    return keys.every((key) => (obj[key] === 0 || obj[key] === null || obj[key] === undefined));
  });
}

const DESCRIPTION =
  'The number of human meals that can be produced from the fish that were landed. (<i>Landed weight</i> × <i>fillet yield</i>) ÷ <i>portion size</i>';

export default function LandedMeals(props: RegistryComponentProps): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<LandedMealsData>(props);

  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;

  const pageStockName = pageData?.data?.stockName as string | undefined;
  const pageTimeSeriesData = pageData?.data?.timeSeriesData as { meals?: unknown[] } | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const filteredMeals = useMemo(
    () => filterMealsByYear(pageTimeSeriesData?.meals, selectedYear),
    [pageTimeSeriesData?.meals, selectedYear]
  );
  const canOpenModal = selectedModel === 'WHAM';
  const onOpenModal = canOpenModal ? () => setShowModal(true) : undefined;

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openMealsModal', handler);
    return () => window.removeEventListener('openMealsModal', handler);
  }, []);
  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;

  return (
    <div className="landed-meals insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Landed Meals</h3>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle" dangerouslySetInnerHTML={{ __html: DESCRIPTION }} />
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.mealsProduced}
        currentValue={insights.mealsProduced}
        statusQuoContent={
          <>
            {formatNumberSigFig(statusQuoDisplay.mealsProduced)}
            <div className="uncertainty-value">
              (± {formatNumberSigFig(statusQuoDisplay.mealsUncertainty)})
            </div>
          </>
        }
        currentContent={
          <>
            {formatNumberSigFig(insights.mealsProduced)}
            <div className="uncertainty-value">
              (± {formatNumberSigFig(insights.mealsUncertainty)})
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenModal}
        clickable={Boolean(onOpenModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />

      {showModal &&
        createPortal(
          <Modal
            title={`MEALS PRODUCED: ${pageStockName ?? 'All Stocks'}`}
            onClose={handleCloseModal}
            size="extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '90vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px' }}
          >
            {pageLoading ? (
              <p className="noscroll-loading-message">Loading data...</p>
            ) : pageError ? (
              <p className="noscroll-error-message">{pageError}</p>
            ) : !filteredMeals.length || isAllZero(filteredMeals) ? (
              <p className="noscroll-no-data-message">Data deficient</p>
            ) : (
              <MealsProducedChart data={filteredMeals} maxHeight={400} />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
}
